<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Course Enrollments" icon="fa-chalkboard-teacher"/>

            <div class="columns mt-6">
                <div class="column is-9-fullhd">
                    <p class="has-text-white" v-if="!enrollments"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your enrollments...</p>
                    <div v-else>
                        <div v-if="activeEnrollments.length === 0">
                            <p class="has-text-white"><i class="far fa-info-circle fa-fw mr-3"></i>You currently have no active Course Enrollments.<br><i class="far fa-fw mr-3"></i><span class="has-text-weight-bold">Browse the available courses in the Online Course Catalog to get started.</span></p>
                        </div>
                        <div class="columns" v-if="activeEnrollments.length">
                            <div class="column">
                                <enrollment :enrollment="enroll" v-for="enroll in activeEnrollments" class="mb-5"/>
                            </div>
                        </div>
                        <section v-if="completedEnrollments.length !== 0" class="pt-4">
                            <h2 class="mb-1 mt-6 mb-6 has-text-white"><i class="fad fa-check-to-slot fa-fw mr-3"></i>Completed Courses</h2>

                            <!-- Grouped by certification > category when category data exists -->
                            <div v-if="completedGroups">
                                <div class="mb-6" v-for="group in completedGroups.groups" :key="'cert-' + group.key">
                                    <h3 v-if="group.title"><i class="fad fa-file-certificate fa-fw mr-3"></i>{{ group.title }}</h3>
                                    <div v-for="category in group.categories" :key="'cat-' + group.key + '-' + category.id">
                                        <h4>{{ category.title }} <span class="count">({{ category.enrollments.length }})</span></h4>
                                        <enrollment :enrollment="enroll" v-for="enroll in category.enrollments" :key="enroll.id" class="mb-5"/>
                                    </div>
                                </div>
                                <div v-if="completedGroups.ungrouped.length">
                                    <h3><i class="fad fa-check-to-slot fa-fw mr-3"></i>More Completed Courses</h3>
                                    <enrollment :enrollment="enroll" v-for="enroll in completedGroups.ungrouped" :key="enroll.id" class="mb-5"/>
                                </div>
                            </div>

                            <!-- Flat fallback when no completed enrollment carries categories -->
                            <template v-else>
                                <enrollment :enrollment="enroll" v-for="enroll in completedEnrollments" class="mb-5"/>
                            </template>
                        </section>

                        <!-- Enrollments retired via the >365-day-expired reactivation path
                             (a new enrollment now carries the course forward) -- effectively
                             dead records at this point, kept visible only as history -->
                        <section v-if="reactivatedEnrollments.length !== 0" class="pt-4">
                            <h2 class="mb-1 mt-6 mb-6 has-text-white"><i class="fad fa-rotate fa-fw mr-3"></i>Reactivated Courses</h2>
                            <enrollment :enrollment="enroll" v-for="enroll in reactivatedEnrollments" class="mb-5"/>
                        </section>

                        <!--                        <b-tabs>-->
                        <!--                            <b-tab-item label="2022">-->

                        <!--                            </b-tab-item>-->

                        <!--                            <b-tab-item label="2021">-->

                        <!--                            </b-tab-item>-->

                        <!--                            <b-tab-item label="2020">-->

                        <!--                            </b-tab-item>-->
                        <!--                        </b-tabs>-->

                    </div>


                </div>
                <div class="column has-text-white has-text-right is-3-fullhd is-hidden-touch is-hidden-desktop-only is-hidden-widescreen-only">
                    <div class="mb-6 ">
                        <router-link :to="{name: 'course-catalog'}">
                            <b-button type="is-light" outlined><i class="fad fa-browsers mr-3"></i>Browse Available Courses</b-button>
                        </router-link>
                    </div>
                </div>
            </div>

        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import MainHeader from "../../components/framework/MainHeader";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import Enrollment from "../../components/Enrollment";

    import {mapActions, mapGetters} from "vuex";

    export default {
        components: {Enrollment, Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                showCompleted: false
            };
        },
        computed: {
            ...mapGetters(['enrollments', 'currentUser', 'categoryGroups']),
            activeEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (!enrollment.completed_at && !enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.started_at) - new Date(a.started_at);
                });

                return enrollments;
            },
            // Retired via the >365-day-expired reactivation path -- a new enrollment
            // now carries the course forward, this one is effectively dead
            reactivatedEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.reactivated_at) - new Date(a.reactivated_at);
                });

                return enrollments;
            },
            completedEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (enrollment.completed_at && !enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.completed_at) - new Date(a.completed_at);
                });

                return enrollments;
            },
            // Completed enrollments grouped by certification > top-level category.
            // Null when no completed enrollment carries category data (flat fallback).
            completedGroups: function() {
                const completed = this.completedEnrollments;
                const hasCategories = (e) => e.courses && e.courses.categories && e.courses.categories.length > 0;
                const withCategories = completed.filter(hasCategories);
                if (withCategories.length === 0) return null;

                // Lookup maps from the grouped category tree in the store
                const certTitles = {};
                const certOrder = {};
                const categoryById = {};
                const categoryOrder = {};
                (this.categoryGroups || []).forEach((group, gi) => {
                    certTitles[group.certification.id] = group.certification.title;
                    certOrder[group.certification.id] = gi;
                    (group.categories || []).forEach((cat, ci) => {
                        categoryById[cat.id] = cat;
                        categoryOrder[cat.id] = ci;
                        for (const child of (cat.children || [])) {
                            categoryById[child.id] = child;
                        }
                    });
                });

                const groups = [];
                const groupMap = {};
                for (const enrollment of withCategories) {
                    // First assigned category only; roll a child up to its top-level parent when known
                    let category = enrollment.courses.categories[0];
                    if (category.parent_id && categoryById[category.parent_id]) {
                        category = categoryById[category.parent_id];
                    }

                    const certId = enrollment.certification_id || category.certification_id || null;
                    const key = certId || 'none';
                    if (!groupMap[key]) {
                        // Fall back to the course's organization title when the
                        // certification is unknown to the category tree
                        let title = certTitles[certId] || null;
                        if (!title && enrollment.courses.organizations) {
                            title = enrollment.courses.organizations.title;
                        }
                        groupMap[key] = {key, certId, title, categories: [], categoryMap: {}};
                        groups.push(groupMap[key]);
                    }

                    const group = groupMap[key];
                    if (!group.categoryMap[category.id]) {
                        group.categoryMap[category.id] = {id: category.id, title: category.title, enrollments: []};
                        group.categories.push(group.categoryMap[category.id]);
                    }
                    group.categoryMap[category.id].enrollments.push(enrollment);
                }

                const order = (map, id) => (map[id] !== undefined ? map[id] : Number.MAX_SAFE_INTEGER);
                groups.sort((a, b) => order(certOrder, a.certId) - order(certOrder, b.certId));
                for (const group of groups) {
                    group.categories.sort((a, b) => order(categoryOrder, a.id) - order(categoryOrder, b.id));
                    for (const category of group.categories) {
                        category.enrollments.sort((a, b) => (a.courses.title.toUpperCase() > b.courses.title.toUpperCase()) ? 1 : -1);
                    }
                }

                return {groups, ungrouped: completed.filter(e => !hasCategories(e))};
            }
        },
        methods: {
            ...mapActions(['getEnrollments', 'getCategories'])
        },
        mounted() {
            this.getEnrollments();
            if (!this.categoryGroups || this.categoryGroups.length === 0) {
                this.getCategories().catch(() => {});
            }
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    h3 {
        font-size: 1rem;
        font-weight: bold;
        color: white;
        padding-bottom: 0.4rem;
        margin: 2rem 0 1.25rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    h4 {
        font-size: 0.9rem;
        font-weight: bold;
        color: white;
        margin: 1.5rem 0 1.25rem;

        .count {
            font-weight: normal;
            color: rgba(255, 255, 255, 0.6);
        }
    }
</style>

<style lang="scss">

</style>
